import { useEffect } from 'react';

interface BreadcrumbItem {
  name: string;
  item: string;
}

interface PageSEOOptions {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  schema?: object | object[];
  breadcrumbs?: BreadcrumbItem[];
  noIndex?: boolean;
}

const BASE_URL = 'https://jeffdume.com';
const DEFAULT_IMAGE = `${BASE_URL}/images/Jeff_D-28.jpg`;
const TWITTER_HANDLE = '@jeffdume';

function setMeta(name: string, content: string, property = false) {
  const attr = property ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setSchema(schema: object | object[], id = 'page') {
  // Remove existing dynamic schema tags for this page
  document.querySelectorAll(`script[data-page-schema="${id}"]`).forEach((el) => el.remove());

  const schemas = Array.isArray(schema) ? schema : [schema];
  schemas.forEach((s) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page-schema', id);
    script.textContent = JSON.stringify(s);
    document.head.appendChild(script);
  });
}

function buildBreadcrumbSchema(breadcrumbs: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
      ...breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 2,
        name: b.name,
        item: b.item.startsWith('http') ? b.item : `${BASE_URL}${b.item}`,
      })),
    ],
  };
}

export function usePageSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  schema,
  breadcrumbs,
  noIndex = false,
}: PageSEOOptions) {
  useEffect(() => {
    // Title
    document.title = title;

    // Primary meta
    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('author', 'Jeff Dumè');
    setMeta(
      'robots',
      noIndex
        ? 'noindex, nofollow'
        : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
    );

    // Geo / Local
    setMeta('geo.region', 'US-TX');
    setMeta('geo.placename', 'Houston, Texas');
    setMeta('geo.position', '29.7604;-95.3698');
    setMeta('ICBM', '29.7604, -95.3698');

    // Canonical
    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : BASE_URL;
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('og:type', ogType, true);
    setMeta('og:site_name', 'Jeff Dumè', true);
    setMeta('og:title', ogTitle || title, true);
    setMeta('og:description', ogDescription || description, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:image', ogImage || DEFAULT_IMAGE, true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('og:image:alt', ogTitle || title, true);
    setMeta('og:image:type', 'image/jpeg', true);
    setMeta('og:locale', 'en_US', true);

    // Twitter / X
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', TWITTER_HANDLE);
    setMeta('twitter:creator', TWITTER_HANDLE);
    setMeta('twitter:title', ogTitle || title);
    setMeta('twitter:description', ogDescription || description);
    setMeta('twitter:image', ogImage || DEFAULT_IMAGE);
    setMeta('twitter:image:alt', ogTitle || title);

    // Page schema
    if (schema) {
      setSchema(schema, 'page');
    }

    // Breadcrumb schema
    if (breadcrumbs && breadcrumbs.length > 0) {
      setSchema(buildBreadcrumbSchema(breadcrumbs), 'breadcrumb');
    }

    return () => {
      document.title = 'Jeff Dumè | Founder of Mainframe Solutions & BuildFit AI';
      document.querySelectorAll('script[data-page-schema]').forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogType, schema, breadcrumbs, noIndex]);
}